// ============================================================================
// The four "why" sentences on the pricing screen (design reference screen 5),
// templated from the figures bid_suggest_price() returns. Bryan, 2026-09-18:
// answer first, method second. Every number here is one the database returned;
// the only comparisons are the words ("close to", "a step up").
// ============================================================================

import type { Suggestion } from './api';
import { money, perSf, pct, num } from './format';

export interface CompForWhy {
  label: string; // "DaVita - Gardner (Shiny Floors Cleaning Service LLC)"
  ors: number | null;
  sqft: number | null;
  revenue: number | null;
  excluded: boolean;
}

export interface WhyLine {
  key: 'similar' | 'cost' | 'labor' | 'watch';
  title: string;
  text: string;
}

interface Read { label: string; per: string; rate: number | null; n: number | null; monthly: number | null }

function reads(s: Suggestion): Read[] {
  return Array.isArray(s.b_reads) ? (s.b_reads as unknown as Read[]) : [];
}

function missingMessages(s: Suggestion): string[] {
  const m = Array.isArray(s.missing) ? (s.missing as unknown as { message: string }[]) : [];
  return m.map((x) => x.message).filter(Boolean);
}

export function whyLines(s: Suggestion, comps: CompForWhy[], market: string | null): WhyLine[] {
  const out: WhyLine[] = [];
  const r = reads(s);
  const sfRates = r.filter((x) => x.per === 'sf' && x.rate != null).map((x) => Number(x.rate));
  const usable = comps.filter((c) => !c.excluded && c.revenue != null).slice(0, 2);

  // 1 · Similar buildings
  let similar: string;
  if (r.length === 0) {
    similar = s.b_note ? `There are ${s.b_note}.` : 'There are no comparable buildings in the book yet.';
  } else {
    const lo = Math.min(...sfRates);
    const hi = Math.max(...sfRates);
    const range = sfRates.length ? (lo === hi ? `${perSf(lo)}/sf` : `${perSf(lo)}–${perSf(hi)}/sf`) : null;
    similar = range
      ? `Buildings like this in the book bill ${range} — ${money(s.b_min)} to ${money(s.b_max)} a month for this one.`
      : `Buildings like this in the book point to ${money(s.b_min)}–${money(s.b_max)} a month.`;
    if (usable.length) {
      similar +=
        ' ' +
        usable
          .map((c) => `${c.label}${c.ors ? ` (${c.ors} OR, ${num(c.sqft)} sf)` : c.sqft ? ` (${num(c.sqft)} sf)` : ''} is at ${money(c.revenue)}`)
          .join('; ') +
        '.';
    }
    if (s.b_note) similar += ` Note: ${s.b_note}.`;
  }
  out.push({ key: 'similar', title: 'Similar buildings', text: similar });

  // 2 · What it costs us
  let cost: string;
  if (s.sub_estimate == null || s.recommended_monthly == null) {
    cost = 'The app cannot estimate the sub cost yet — ' + (missingMessages(s)[0] ?? 'the building details are incomplete.');
  } else {
    cost = `A ${market ?? 'local'} sub should take this building for about ${money(s.sub_estimate)} a month. At our usual ${pct(s.target_margin)} margin that is ${money(s.recommended_monthly)}.`;
    cost += s.floor_monthly != null
      ? ` Below ${money(s.floor_monthly)} we are under ${pct(s.floor_margin)}.`
      : ' There is no floor margin set for this kind of building yet, so there is no "don\'t go below" number.';
  }
  out.push({ key: 'cost', title: 'What it costs us', text: cost });

  // 3 · The labor check
  let labor: string;
  if (s.facility_type === 'Dialysis') {
    labor = 'Dialysis is priced per square foot by nights cleaned, so there is no crew-hour check.';
  } else if (s.c_sub == null) {
    labor = s.c_hours_per_night != null
      ? `About ${num(s.c_hours_per_night)} crew-hours a night, but there is no labour rate for ${market ?? 'this market'} to turn that into a cost.`
      : 'Not enough building detail for a crew-hour check yet.';
  } else {
    const gap = s.sub_estimate ? Number(s.c_sub) / Number(s.sub_estimate) : null;
    const verdict = gap == null ? '' : gap > 1.15 ? ' — well above the estimate above; worth asking why before trusting either' : gap < 0.85 ? ' — well below the estimate above; the sub may have room' : ' — close to the estimate above, so nothing is off';
    labor = `About ${num(s.c_hours_per_night)} crew-hours a night. At ${market ?? 'local'} wages that is ~${money(s.c_sub)} of sub cost${verdict}.`;
  }
  out.push({ key: 'labor', title: 'The labor check', text: labor });

  // 4 · Watch out
  let watch: string;
  if (s.incumbent_monthly_rate != null && s.premium_recommended_pct != null) {
    const step = (p: number | null | undefined) => (p == null ? '' : Number(p) >= 0 ? `a ${pct(p)} step up` : `${pct(-Number(p))} below`);
    watch = `They pay ${money(s.incumbent_monthly_rate)} today. ${money(s.recommended_monthly)} is ${step(s.premium_recommended_pct)}; ${money(s.top_monthly)} is ${step(s.premium_top_pct)}.`;
  } else {
    watch = 'We do not know what they pay today — ask; it is the anchor in every conversation.';
  }
  if (s.sqft_estimated) watch += ` The square footage is an estimate from the chair count until the walkthrough measures it.`;
  const miss = missingMessages(s).filter((m) => !cost.includes(m));
  if (miss.length) watch += ' ' + miss.join(' ');
  out.push({ key: 'watch', title: 'Watch out', text: watch });

  return out;
}
